import { GetStaticPaths, GetStaticProps } from 'next';
import React, { useEffect } from 'react';

import RichText from 'components/RichText/RichText';
import { ModuleListProps } from 'containers/InsightPage/InsightPage.types';
import { PrivacyType } from 'containers/Privacy/Privacy.types';
import IndentTextSection from 'containers/Privacy/sections/IndentTextSection/IndentTextSection';
import TableSection from 'containers/Privacy/sections/TableSection/TableSection';
import TextBlockSection from 'containers/Privacy/sections/TextBlockSection/TextBlockSection';
import { useCopyStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { queryCMS } from 'utils/cms/api';
import { privacy } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { pageMotionProps } from 'utils/styles/animations';

import * as S from './Privacy.styles';

export interface PrivacyProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  page: PrivacyType;
}

const defaultProps: Partial<PrivacyProps> = {};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const id = await marketPageId(MarketPages.privacyPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const response = await queryCMS({
    query: privacy({ id, locale, slug: params?.slug, globalIds }),
  });

  const {
    data: {
      sharedCopies,
      privacies: page,
      redirectPopups,
      cookieBar,
      navbar,
      footer,
    },
  } = response;

  if (!page || !sharedCopies) return { notFound: true };

  return {
    props: {
      initialCopy: {
        head: page?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      page: page[0] || {},
    },
    revalidate: ISR_TIMEOUT,
  };
};

const Privacy: React.FunctionComponent<PrivacyProps> = ({
  initialCopy,
  page,
}) => {
  const { setCopy } = useCopyStore();

  useEffect(() => {
    setCopy(initialCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  const modulesList: ModuleListProps = {
    ComponentSectionsIndentedTextSection: IndentTextSection,
    ComponentSectionsPrivacyTextBlockSection: TextBlockSection,
    ComponentSectionsTableSection: TableSection,
  };

  const renderModules = () =>
    page?.modules?.map((module, idx) => {
      const moduleName = module.__typename;
      const ModuleComponent = modulesList[moduleName];

      return (
        ModuleComponent && (
          <ModuleComponent key={`${module.__typename}${idx}`} data={module} />
        )
      );
    });

  return (
    <S.Wrapper {...pageMotionProps}>
      <S.HeadingWrapper>
        <S.Title>{page?.title}</S.Title>
        <RichText richText={page?.description} />
      </S.HeadingWrapper>
      {renderModules()}
    </S.Wrapper>
  );
};

Privacy.defaultProps = defaultProps;

export default Privacy;
